import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

export function BotSettings() {
  const [settings, setSettings] = useState({
    bot_prompt: "",
    bot_settings: {
      max_response_length: 200,
      enable_context_selection: true,
      default_language: "es",
      allowed_roles: ["admin"],
      enable_audit_logging: true,
      max_tokens_per_request: 1000
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchBotSettings();
  }, []);

  const fetchBotSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('bot_prompt, bot_settings')
        .not('bot_prompt', 'is', null)
        .limit(1);

      if (error) throw error;
      
      if (data && data.length > 0) {
        setSettings({
          bot_prompt: data[0].bot_prompt || "",
          bot_settings: data[0].bot_settings || {
            max_response_length: 200,
            enable_context_selection: true,
            default_language: "es",
            allowed_roles: ["admin"],
            enable_audit_logging: true,
            max_tokens_per_request: 1000
          }
        });
      }
    } catch (error) {
      console.error('Error fetching bot settings:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los ajustes del bot",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Primero obtenemos el ID del registro existente
      const { data: existingData, error: fetchError } = await supabase
        .from('app_settings')
        .select('id')
        .not('bot_prompt', 'is', null)
        .limit(1);

      if (fetchError) throw fetchError;

      if (existingData && existingData.length > 0) {
        // Actualizamos el registro existente
        const { error: updateError } = await supabase
          .from('app_settings')
          .update({
            bot_prompt: settings.bot_prompt,
            bot_settings: settings.bot_settings
          })
          .eq('id', existingData[0].id);

        if (updateError) throw updateError;
      } else {
        // Creamos un nuevo registro
        const { error: insertError } = await supabase
          .from('app_settings')
          .insert({
            bot_prompt: settings.bot_prompt,
            bot_settings: settings.bot_settings
          });

        if (insertError) throw insertError;
      }

      toast({
        title: "Éxito",
        description: "Configuración del bot guardada correctamente"
      });
    } catch (error) {
      console.error('Error saving bot settings:', error);
      toast({
        title: "Error",
        description: "No se pudieron guardar los ajustes del bot",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const updateBotSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      bot_settings: {
        ...prev.bot_settings,
        [key]: value
      }
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Cargando configuración del bot...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Configuración del Bot de Chat</CardTitle>
          <CardDescription>
            Configura el comportamiento y las respuestas del bot de chat
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Prompt del Bot */}
          <div className="space-y-2">
            <Label htmlFor="bot_prompt">Prompt Principal del Bot</Label>
            <Textarea
              id="bot_prompt"
              value={settings.bot_prompt}
              onChange={(e) => setSettings(prev => ({ ...prev, bot_prompt: e.target.value }))}
              placeholder="Eres un asistente especializado en gestión de almacén..."
              rows={6}
              className="font-mono text-sm"
            />
            <p className="text-sm text-gray-500">
              Define las instrucciones principales que seguirá el bot al responder
            </p>
          </div>

          {/* Configuración Avanzada */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Configuración Avanzada</h3>
            
            {/* Longitud máxima de respuesta */}
            <div className="space-y-2">
              <Label htmlFor="max_response_length">Longitud Máxima de Respuesta (caracteres)</Label>
              <Input
                id="max_response_length"
                type="number"
                value={settings.bot_settings.max_response_length}
                onChange={(e) => updateBotSetting('max_response_length', parseInt(e.target.value))}
                min="50"
                max="500"
              />
            </div>

            {/* Tokens máximos por petición */}
            <div className="space-y-2">
              <Label htmlFor="max_tokens_per_request">Tokens Máximos por Petición</Label>
              <Input
                id="max_tokens_per_request"
                type="number"
                value={settings.bot_settings.max_tokens_per_request}
                onChange={(e) => updateBotSetting('max_tokens_per_request', parseInt(e.target.value))}
                min="100"
                max="4000"
              />
            </div>

            {/* Funcionalidades */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="enable_context_selection"
                  checked={settings.bot_settings.enable_context_selection}
                  onCheckedChange={(checked) => updateBotSetting('enable_context_selection', checked)}
                />
                <Label htmlFor="enable_context_selection">
                  Habilitar Selección de Contexto
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="enable_audit_logging"
                  checked={settings.bot_settings.enable_audit_logging}
                  onCheckedChange={(checked) => updateBotSetting('enable_audit_logging', checked)}
                />
                <Label htmlFor="enable_audit_logging">
                  Habilitar Registro de Auditoría
                </Label>
              </div>
            </div>

            {/* Roles permitidos */}
            <div className="space-y-2">
              <Label>Roles Permitidos</Label>
              <div className="space-y-2">
                {['admin', 'vendedor', 'cliente'].map((role) => (
                  <div key={role} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`role_${role}`}
                      checked={settings.bot_settings.allowed_roles.includes(role)}
                      onChange={(e) => {
                        const currentRoles = settings.bot_settings.allowed_roles;
                        const newRoles = e.target.checked
                          ? [...currentRoles, role]
                          : currentRoles.filter(r => r !== role);
                        updateBotSetting('allowed_roles', newRoles);
                      }}
                    />
                    <Label htmlFor={`role_${role}`} className="capitalize">
                      {role}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving}>
          {saving ? "Guardando..." : "Guardar Configuración"}
        </Button>
      </div>
    </form>
  );
} 